import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { getArticleById, getArticlesByCategory } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = getArticlesByCategory(article.categorySlug)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 黄瓜视频`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://huanggua.video/article/${article.id}`}
        ogType="article"
        ogImage={article.thumbnail}
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishDate,
            image: article.thumbnail,
            publisher: { '@type': 'Organization', name: '黄瓜视频' },
          },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100/60 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category
                  ? [{ label: category.name, href: `/category/${category.slug}` }]
                  : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="py-8 md:py-12">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.publishDate}</span>
              </div>

              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-600">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="font-medium">{article.author}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.duration}
                </span>
              </div>
            </div>

            <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer">
                <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center hover:bg-primary-600 transition-colors">
                  <i className="ri-play-fill text-background-50 text-2xl ml-1"></i>
                </div>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200/60">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-background-200 transition-colors cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-6 mt-6 py-4 border-t border-background-200/60">
              <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-heart-line text-lg"></i>
                <span>{article.likes >= 10000 ? `${(article.likes / 10000).toFixed(1)}万` : article.likes}</span>
              </button>
              <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-chat-1-line text-lg"></i>
                <span>{article.comments}</span>
              </button>
              <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-share-forward-line text-lg"></i>
                <span>分享</span>
              </button>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 bg-background-100/60">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-foreground-900 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={rel.thumbnail}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-xs font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-[11px] text-foreground-500 mt-1">{rel.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}